import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import type Customer from "@acto/taskpane/core/domain/Customer";
import { useDateFormatter } from "@acto/taskpane/field-sales-2/utils/hooks/useDateFormatter";
import { EM_DASH } from "./emDash";

const Columns = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[4],
  width: "100%",
}));

const Column = styled("div")(({ theme }) => ({
  flex: "1 1 0",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[4],
}));

const Label = styled("span")(({ theme }) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[168],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const Value = styled("span")(({ theme }) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.hairline,
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

interface CustomerActivityMetaProps {
  customer: Customer;
  lastOrderAt?: Date | null;
}

export function CustomerActivityMeta({ customer, lastOrderAt }: CustomerActivityMetaProps) {
  const t = useTranslations("fieldSalesTwo.customerCard");
  const { formatDayShortMonthYear, formatRelativeDays } = useDateFormatter();

  const lastVisitAt = customer.lastVisitDate ? new Date(customer.lastVisitDate) : null;

  return (
    <Columns>
      <Column>
        <Label>{t("lastVisit")}</Label>
        <Value>{lastVisitAt ? formatDayShortMonthYear(lastVisitAt) : EM_DASH}</Value>
      </Column>
      <Column>
        <Label>{t("lastOrder")}</Label>
        <Value>{lastOrderAt ? formatRelativeDays(lastOrderAt) : EM_DASH}</Value>
      </Column>
    </Columns>
  );
}
